import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { app } from '../firebase';
import competitionManager from '../utils/competitionManager';
import ideaBg from '../assets/idea-bg3.png';
import RestrictionModal from '../components/RestrictionModal';

import './CompetitionTimer.css';

const CompetitionTimer = () => {
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, phase: 'competing' });
  const [isRegistered, setIsRegistered] = useState(false);
  const [restriction, setRestriction] = useState(null);

  const navigate = useNavigate();
  const auth = getAuth(app);
  const db = getFirestore(app);

  /* ===== LOAD USER STATUS ===== */
  useEffect(() => {
    const load = async () => {
      const user = auth.currentUser;
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const registered = await competitionManager.isUserRegistered(user.uid);
          setIsRegistered(registered);
        }
      } catch (err) {
        console.error("Error loading user status:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [auth.currentUser, db]);

  /* ===== DIGITAL TIMER (DAYS / HOURS / MINUTES) ===== */
  const updateTime = () => {
    try {
      const time = competitionManager.getTimeRemaining();
      setTimeLeft({
        days: time.days ?? 0,
        hours: time.hours ?? 0,
        minutes: time.minutes ?? 0,
        phase: time.phase ?? 'competing',
      });
    } catch {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, phase: 'competing' });
    }
  };

  useEffect(() => {
    updateTime();
    // 30s refresh keeps the minute digit accurate without hammering the calc
    const t = setInterval(updateTime, 30000);
    return () => clearInterval(t);
  }, []);

  const pad = (n) => String(n).padStart(2, '0');

  /* ===== NAVIGATION / RESTRICTIONS ===== */
  const handleRegister = (e) => {
    e.stopPropagation();

    if (!auth.currentUser) {
      navigate('/Login_page');
      return;
    }

    if (timeLeft.phase === 'break') {
      setRestriction({
        variant: 'break',
        stampText: 'On Break',
        message: `The competition is on a short break. Next competition starts in ${timeLeft.days}d ${timeLeft.hours}h ${timeLeft.minutes}m.`,
      });
      return;
    }

    if (isRegistered) {
      setRestriction({
        variant: 'limit',
        stampText: 'One Entry Only',
        message: 'You can only join once per competition. Come back next round!',
      });
      return;
    }

    navigate('/add');
  };

  // --- SKELETON RENDER ---
  if (loading) {
    return (
      <div className="trescend-hero skeleton-card-base">
        <div className="sky-title">
          <div className="skeleton-line skeleton-shimmer" style={{ width: '200px', height: '20px' }}></div>
        </div>
        <div className="ground-panel">
          <div className="skeleton-digital-timer skeleton-shimmer"></div>
        </div>
        <div className="register-btn-wrap">
          <div className="skeleton-register-btn skeleton-shimmer"></div>
        </div>
      </div>
    );
  }

  const isBreak = timeLeft.phase === 'break';
  const buttonLabel = isBreak
    ? 'ON BREAK'
    : isRegistered
      ? 'ALREADY REGISTERED'
      : 'REGISTER NOW';

  // --- ACTUAL RENDER ---
  return (
    <div
      className="trescend-hero"
      style={{ backgroundImage: `url(${ideaBg})` }}
    >
      <div className="sky-title">
        <h1>Your idea deserves recognition</h1>
      </div>

      <div className="ground-panel">
        <div className="timer-eyebrow">
          {isBreak
            ? `Next competition starts in ${timeLeft.days} day${timeLeft.days === 1 ? '' : 's'}`
            : `Competition ends in ${timeLeft.days} day${timeLeft.days === 1 ? '' : 's'}`}
        </div>

        <div className="digital-timer">
          <div className="digital-timer-segment">
            <span className="digital-timer-value">{pad(timeLeft.days)}</span>
            <span className="digital-timer-label">Days</span>
          </div>
          <span className="digital-timer-colon">:</span>
          <div className="digital-timer-segment">
            <span className="digital-timer-value">{pad(timeLeft.hours)}</span>
            <span className="digital-timer-label">Hrs</span>
          </div>
          <span className="digital-timer-colon">:</span>
          <div className="digital-timer-segment">
            <span className="digital-timer-value">{pad(timeLeft.minutes)}</span>
            <span className="digital-timer-label">Min</span>
          </div>
        </div>
      </div>

      <div className="register-btn-wrap">
        <button
          className="register-btn"
          onClick={handleRegister}
        >
          {buttonLabel}
        </button>
      </div>

      <RestrictionModal
        open={!!restriction}
        variant={restriction?.variant}
        stampText={restriction?.stampText}
        message={restriction?.message}
        onClose={() => setRestriction(null)}
      />
    </div>
  );
};

export default CompetitionTimer;